import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSidebar } from '@/contexts/SidebarContext';
import axios from 'axios';
import {
  LayoutDashboard,
  FileText,
  ClipboardList,
  Package,
  CreditCard,
  BarChart3,
  Bell,
  Settings,
  HelpCircle,
  Mail,
  Sparkles,
  Users,
  Store,
  Shield,
  Clock,
  MessageSquare,
  Palette,
  FileCheck,
  Building2,
  ChevronRight,
  Layers,
  User,
  GitBranch,
  CalendarDays,
  Activity,
  Image,
  FlaskConical,
  Briefcase,
  FolderOpen
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/marketplace`;

// Navigation Group Component
const NavGroup = ({ title, children }) => (
  <div className="mb-6">
    {title && (
      <h3 className="px-4 mb-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
        {title}
      </h3>
    )}
    <div className="space-y-1">
      {children}
    </div>
  </div>
);

// Navigation Item Component
const NavItem = ({ path, icon: Icon, label, badge, disabled, isActive, onClick }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 group relative ${
      disabled
        ? 'text-slate-300 cursor-not-allowed'
        : isActive
          ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/25'
          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
    }`}
  >
    <Icon className={`w-5 h-5 transition-transform ${isActive ? '' : 'group-hover:scale-110'}`} />
    <span className="font-medium text-sm flex-1 text-left">{label}</span>
    {badge && (
      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
        badge === 'NEW' 
          ? isActive 
            ? 'bg-white/20 text-white' 
            : 'bg-gradient-to-r from-violet-500 to-purple-500 text-white'
          : badge === 'SOON'
            ? 'bg-slate-200 text-slate-500'
            : isActive
              ? 'bg-white/20 text-white'
              : 'bg-blue-600 text-white'
      }`}>
        {typeof badge === 'number' ? (badge > 99 ? '99+' : badge) : badge}
      </span>
    )}
    {isActive && (
      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-r-full" />
    )}
  </button>
);

export default function Sidebar() {
  const { user } = useAuth();
  const { isOpen, close } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [companyFeatures, setCompanyFeatures] = useState(() => {
    try {
      const cached = localStorage.getItem('bn_company_features');
      return cached ? JSON.parse(cached) : { questionnaires_enabled: false };
    } catch { return { questionnaires_enabled: false }; }
  });

  useEffect(() => {
    if (user && user.user_type !== 'admin') {
      fetchUnreadCount();
      fetchCompanyFeatures();
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${API}/conversations/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const fetchCompanyFeatures = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${API}/company/features`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanyFeatures(response.data);
      localStorage.setItem('bn_company_features', JSON.stringify(response.data));
    } catch (error) {
      console.error('Error fetching company features:', error);
    }
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Brand Manager Navigation
  const brandManagerNav = {
    main: [
      { path: '/marketplace/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    ],
    procurement: [
      { path: '/marketplace/request-service', icon: FileText, label: 'Request Service' },
      { path: '/marketplace/multimodal-rfq', icon: Layers, label: 'Multimodal RFP' },
      { path: '/marketplace/my-rfqs', icon: ClipboardList, label: 'My RFPs' },
      { path: '/marketplace/quotations', icon: Package, label: 'Quotations' },
      { path: '/marketplace/orders', icon: Package, label: 'My Orders' },
      { path: '/marketplace/v2-proposals', icon: Package, label: 'Managed Proposals' },
    ],
    communication: [
      { path: '/marketplace/messages', icon: MessageSquare, label: 'Messages', badge: unreadCount || null },
      { path: '/marketplace/notifications', icon: Bell, label: 'Notifications' },
    ],
    tools: [
      { path: '/marketplace/ai-design', icon: Sparkles, label: 'AI Event Design' },
      { path: '/marketplace/ai-visual-design', icon: Image, label: 'AI Visual Design', badge: 'NEW' },
      { path: '/marketplace/ai-study-design', icon: FlaskConical, label: 'AI Study Design', badge: 'NEW' },
      { path: '/marketplace/prefunctional-design', icon: Building2, label: 'Lobby Design' },
      ...(companyFeatures.questionnaires_enabled ? [{ path: '/marketplace/customer/questionnaires', icon: FileCheck, label: 'Questionnaires', badge: 'NEW' }] : []),
      ...(user?.is_parent ? [{ path: '/marketplace/team', icon: Users, label: 'Team Management' }] : []),
    ],
    analytics: [
      { path: '/marketplace/calendar', icon: CalendarDays, label: 'Calendar', badge: 'NEW' },
      { path: '/marketplace/reports', icon: BarChart3, label: 'Analytics' },
      { path: '/marketplace/payments', icon: CreditCard, label: 'Payments', badge: 'SOON', disabled: true },
    ],
    settings: [
      { path: '/marketplace/my-profile', icon: User, label: 'My Profile' },
      { path: '/marketplace/help', icon: HelpCircle, label: 'Help Center' },
    ],
  };

  // Vendor Navigation
  const vendorNav = {
    main: [
      { path: '/marketplace/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    ],
    business: [
      { path: '/marketplace/quotations', icon: ClipboardList, label: 'Opportunities' },
      { path: '/marketplace/orders', icon: Package, label: 'Projects' },
      { path: '/marketplace/workflow', icon: GitBranch, label: 'Workflow', badge: 'NEW' },
      { path: '/marketplace/vendor/documents', icon: FileCheck, label: 'Documents' },
      { path: '/marketplace/v2-invitations', icon: FileText, label: 'Invitations V2', badge: 'V2' },
    ],
    communication: [
      { path: '/marketplace/messages', icon: MessageSquare, label: 'Messages', badge: unreadCount || null },
      { path: '/marketplace/notifications', icon: Bell, label: 'Notifications' },
    ],
    analytics: [
      { path: '/marketplace/calendar', icon: CalendarDays, label: 'Calendar', badge: 'NEW' },
      { path: '/marketplace/reports', icon: BarChart3, label: 'Analytics' },
      { path: '/marketplace/payments', icon: CreditCard, label: 'Payments', badge: 'SOON', disabled: true },
    ],
    settings: [
      { path: '/marketplace/settings', icon: Settings, label: 'Settings' },
      { path: '/marketplace/my-profile', icon: User, label: 'My Profile' },
      { path: '/marketplace/help', icon: HelpCircle, label: 'Help Center' },
    ],
  };

  // Admin Navigation
  const adminNav = {
    main: [
      { path: '/marketplace/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    ],
    users: [
      { path: '/marketplace/admin/user-management', icon: Users, label: 'User Management', badge: 'NEW' },
      { path: '/marketplace/admin/customers', icon: Users, label: 'Customers' },
      { path: '/marketplace/admin/vendors', icon: Store, label: 'Vendors' },
      { path: '/marketplace/admin/vendor-verification', icon: FileCheck, label: 'Vendor Verification' },
      { path: '/marketplace/admin/pending-approvals', icon: Clock, label: 'Pending Approvals' },
    ],
    operations: [
      { path: '/marketplace/admin/rfqs', icon: FileText, label: 'RFP Management' },
      { path: '/marketplace/admin/managed-rfps', icon: Shield, label: 'Managed Sourcing', badge: 'NEW' },
      { path: '/marketplace/admin/v2-sourcing', icon: Package, label: 'Sourcing V2', badge: 'V2' },
      { path: '/marketplace/admin/v2-offers', icon: FileText, label: 'Offers V2', badge: 'V2' },
      { path: '/marketplace/admin/managed-jobs', icon: Package, label: 'Managed Jobs', badge: 'NEW' },
      { path: '/marketplace/admin/managed-projects', icon: FolderOpen, label: 'Multimodal Projects', badge: 'NEW' },
      { path: '/marketplace/admin/orders', icon: Package, label: 'Order Management' },
      { path: '/marketplace/workflow', icon: GitBranch, label: 'All Workflows' },
      { path: '/marketplace/admin/documents', icon: FileCheck, label: 'Documents', badge: 'NEW' },
    ],
    platform: [
      { path: '/marketplace/admin/design-settings', icon: Palette, label: 'AI Design Settings' },
      { path: '/marketplace/admin/nda-settings', icon: Shield, label: 'NDA & Modes', badge: 'NEW' },
      { path: '/marketplace/admin/terms-conditions', icon: FileText, label: 'Terms & Conditions', badge: 'NEW' },
      { path: '/marketplace/admin/email-notifications', icon: Mail, label: 'Email Notifications', badge: 'NEW' },
      { path: '/marketplace/messages', icon: MessageSquare, label: 'All Messages' },
      { path: '/marketplace/admin/admins', icon: Shield, label: 'Admin Users' },
    ],
    analytics: [
      { path: '/marketplace/calendar', icon: CalendarDays, label: 'Calendar', badge: 'NEW' },
      { path: '/marketplace/reports', icon: BarChart3, label: 'Reports' },
      { path: '/marketplace/admin/monitoring', icon: Activity, label: 'System Monitoring', badge: 'LIVE' },
      { path: '/marketplace/settings', icon: Settings, label: 'System Settings' },
    ],
  };

  const getNavConfig = () => {
    if (!user) return {};
    if (user.user_type === 'brand_manager') return brandManagerNav;
    if (user.user_type === 'vendor') return vendorNav;
    if (user.user_type === 'admin') return adminNav;
    return {};
  };

  const navConfig = getNavConfig();

  const renderNavItems = (items, groupTitle) => (
    <NavGroup title={groupTitle} key={groupTitle}>
      {items.map((item) => (
        <NavItem
          key={item.path}
          {...item}
          isActive={isActive(item.path)}
          onClick={() => { if (!item.disabled) { navigate(item.path); close(); } }}
        />
      ))}
    </NavGroup>
  );

  return (
    <>
      {/* Mobile backdrop overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden backdrop-blur-sm"
          onClick={close}
          data-testid="sidebar-backdrop"
        />
      )}
      <aside 
        className={`
          fixed top-0 left-0 z-50 w-72 bg-white border-r border-slate-200/80 h-full overflow-y-auto scrollbar-premium
          transform transition-transform duration-300 ease-in-out
          lg:static lg:z-auto lg:w-64 lg:h-[calc(100vh-64px)] lg:sticky lg:top-16 lg:translate-x-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        data-testid="premium-sidebar"
      >
        {/* Mobile header with close button */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200/80 lg:hidden">
          <div className="flex items-center gap-3">
            <img src="/logo-icon.png" alt="Brand Nexus" className="h-[42px] w-[42px] object-contain" />
            <span className="font-extrabold text-slate-900 text-[18px] tracking-[2px]">BRAND NEXUS</span>
          </div>
          <button
            onClick={close}
            className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
            data-testid="sidebar-close-btn"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <nav className="p-4">
        {/* Main navigation */}
        {navConfig.main && renderNavItems(navConfig.main, null)}
        
        {/* Role-specific groups */}
        {user?.user_type === 'brand_manager' && (
          <>
            {renderNavItems(navConfig.procurement, 'Procurement')}
            {renderNavItems(navConfig.communication, 'Communication')}
            {renderNavItems(navConfig.tools, 'AI Tools')}
            {renderNavItems(navConfig.analytics, 'Analytics')}
            {renderNavItems(navConfig.settings, 'Settings')}
          </>
        )}
        
        {user?.user_type === 'vendor' && (
          <>
            {renderNavItems(navConfig.business, 'Business')}
            {renderNavItems(navConfig.communication, 'Communication')}
            {renderNavItems(navConfig.analytics, 'Analytics')}
            {renderNavItems(navConfig.settings, 'Settings')}
          </>
        )}
        
        {user?.user_type === 'admin' && (
          <>
            {renderNavItems(navConfig.users, 'User Management')}
            {renderNavItems(navConfig.operations, 'Operations')}
            {renderNavItems(navConfig.platform, 'Platform')}
            {renderNavItems(navConfig.analytics, 'Analytics & Settings')}
          </>
        )}
      </nav>

      {/* Sidebar Footer - Upgrade CTA for Brand Managers */}
      {user?.user_type === 'brand_manager' && (
        <div className="p-4 border-t border-slate-100">
          <div className="p-4 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl text-white">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-5 h-5" />
              <span className="font-semibold text-sm">AI Credits</span>
            </div>
            <p className="text-xs text-blue-100 mb-3">
              Generate stunning event designs with AI
            </p>
            <button 
              onClick={() => { navigate('/marketplace/ai-design'); close(); }}
              className="w-full py-2 px-3 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors"
            >
              View Credits
            </button>
          </div>
        </div>
      )}
    </aside>
    </>
  );
}
